import { AxiosError } from 'axios';

export type ErrorCategory = 'network' | 'validation' | 'authentication' | 'authorization' | 'server' | 'client' | 'unknown';
export type ErrorContext = 'upload' | 'analysis' | 'login' | 'register' | 'project' | 'document' | 'issue' | 'export' | 'general';

export interface ProcessedError {
  message: string;
  userMessage: string;
  category: ErrorCategory;
  context: ErrorContext;
  code?: string | number;
  retryable: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  details?: string;
  errorId?: string;
}

// User-friendly error message mappings
const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  'ERR_NETWORK': 'Unable to connect to the server. Please check your internet connection and try again.',
  'ERR_INTERNET_DISCONNECTED': 'No internet connection. Please check your connection and try again.',
  'ERR_CONNECTION_REFUSED': 'Could not connect to the server. The service may be temporarily unavailable.',
  'ECONNABORTED': 'The request timed out. Please try again.',
  'ERR_TIMEOUT': 'The request took too long to complete. Please try again.',
  
  // HTTP status codes
  400: 'Invalid request. Please check your input and try again.',
  401: 'You need to log in to access this feature.',
  403: 'You don\'t have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This action conflicts with the current state. Please refresh and try again.',
  413: 'The file is too large. Please upload a smaller file.',
  415: 'This file type is not supported. Please upload a PDF or Word document.',
  422: 'The information provided is invalid. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'A server error occurred. Please try again in a few moments.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
  504: 'The request timed out. Please try again.',

  // Context-specific errors
  'FILE_TOO_LARGE': 'File is too large. Maximum size is 100MB.',
  'INVALID_FILE_TYPE': 'File type not supported. Please upload PDF or Word documents only.',
  'UPLOAD_FAILED': 'File upload failed. Please try again.',
  'ANALYSIS_FAILED': 'Document analysis failed. Please try again.',
  'LOGIN_FAILED': 'Login failed. Please check your credentials.',
  'REGISTER_FAILED': 'Registration failed. Please try again.',
  'PROJECT_NOT_FOUND': 'Project not found. It may have been deleted.',
  'DOCUMENT_NOT_FOUND': 'Document not found. It may have been deleted.',
  'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
  'EMAIL_ALREADY_EXISTS': 'An account with this email address already exists.',
  'WEAK_PASSWORD': 'Password must be at least 8 characters long.',
  'INVALID_EMAIL': 'Please enter a valid email address.',
};

// Context-specific error message templates
const CONTEXT_TEMPLATES: Record<ErrorContext, Record<string, string>> = {
  upload: {
    default: 'File upload failed. Please try again.',
    network: 'Upload failed due to connection issues. Please check your internet and try again.',
    validation: 'The file you\'re trying to upload is invalid. Please check the file type and size.',
    server: 'Upload failed due to a server error. Please try again in a few moments.'
  },
  analysis: {
    default: 'Document analysis failed. Please try again.',
    network: 'Analysis failed due to connection issues. Please try again.',
    validation: 'Analysis failed due to invalid configuration. Please check your settings.',
    server: 'Analysis failed due to a server error. Please try again in a few moments.'
  },
  login: {
    default: 'Login failed. Please check your credentials and try again.',
    network: 'Login failed due to connection issues. Please try again.',
    validation: 'Please enter a valid email and password.',
    authentication: 'Invalid email or password. Please try again.'
  },
  register: {
    default: 'Registration failed. Please try again.',
    network: 'Registration failed due to connection issues. Please try again.',
    validation: 'Please check your registration information and try again.',
    server: 'Registration failed due to a server error. Please try again in a few moments.'
  },
  project: {
    default: 'Project operation failed. Please try again.',
    network: 'Project operation failed due to connection issues. Please try again.',
    authorization: 'You don\'t have permission to access this project.',
    server: 'Project operation failed due to a server error. Please try again in a few moments.'
  },
  document: {
    default: 'Document operation failed. Please try again.',
    network: 'Document operation failed due to connection issues. Please try again.',
    authorization: 'You don\'t have permission to access this document.',
    server: 'Document operation failed due to a server error. Please try again in a few moments.'
  },
  issue: {
    default: 'Issue operation failed. Please try again.',
    network: 'Issue operation failed due to connection issues. Please try again.',
    server: 'Issue operation failed due to a server error. Please try again in a few moments.'
  },
  export: {
    default: 'Export failed. Please try again.',
    network: 'Export failed due to connection issues. Please try again.',
    server: 'Export failed due to a server error. Please try again in a few moments.'
  },
  general: {
    default: 'An unexpected error occurred. Please try again.',
    network: 'Connection issues detected. Please check your internet and try again.',
    server: 'A server error occurred. Please try again in a few moments.'
  }
};

/**
 * Categorizes an error based on its properties
 */
export const categorizeError = (error: any): ErrorCategory => {
  if (!error) return 'unknown';

  // Network errors
  if (!navigator.onLine) return 'network';
  if (error.code === 'ERR_NETWORK' || error.code === 'ERR_INTERNET_DISCONNECTED') return 'network';
  if (error.code === 'ERR_CONNECTION_REFUSED' || error.code === 'ECONNABORTED') return 'network';
  
  // Axios errors
  if (error.isAxiosError || error.response) {
    const status = error.response?.status;
    if (status === 401) return 'authentication';
    if (status === 403) return 'authorization';
    if (status >= 400 && status < 500) return 'validation';
    if (status >= 500) return 'server';
  }

  // Generic categorization based on error message
  const message = error.message?.toLowerCase() || '';
  if (message.includes('network') || message.includes('connection')) return 'network';
  if (message.includes('unauthorized') || message.includes('login')) return 'authentication';
  if (message.includes('forbidden') || message.includes('permission')) return 'authorization';
  if (message.includes('validation') || message.includes('invalid')) return 'validation';
  if (message.includes('server') || message.includes('internal')) return 'server';

  return 'client';
};

/**
 * Determines if an error is retryable
 */
export const isRetryableError = (error: any, category: ErrorCategory): boolean => {
  if (!error) return false;

  // Network errors are usually retryable
  if (category === 'network') return true;

  // Server errors are retryable
  if (category === 'server') return true;

  // Specific status codes that are retryable
  const status = error.response?.status;
  if (status === 408 || status === 409 || status === 429 || status >= 500) return true;

  // Timeout errors are retryable
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_TIMEOUT') return true;

  return false;
};

/**
 * Determines error severity
 */
export const getErrorSeverity = (error: any, category: ErrorCategory): 'low' | 'medium' | 'high' | 'critical' => {
  if (!error) return 'low';

  const status = error.response?.status;

  // Critical errors that prevent core functionality
  if (category === 'authentication' && status === 401) return 'high';
  if (category === 'server' && status >= 500) return 'high';
  if (category === 'network' && !navigator.onLine) return 'critical';

  // Medium severity errors
  if (category === 'authorization') return 'medium';
  if (category === 'validation') return 'medium';
  if (category === 'network') return 'medium';

  return 'low';
};

/**
 * Gets a user-friendly error message
 */
export const getUserFriendlyMessage = (
  error: any, 
  category: ErrorCategory, 
  context: ErrorContext = 'general'
): string => {
  if (!error) return CONTEXT_TEMPLATES[context].default;

  // Check for backend structured error response first
  const backendError = error.response?.data?.error;
  if (backendError?.error_code) {
    switch (backendError.error_code) {
      case 'WEAK_PASSWORD':
        return 'Password must be at least 8 characters long.';
      case 'EMAIL_EXISTS':
        return 'An account with this email already exists';
      case 'INVALID_EMAIL':
        return 'Please enter a valid email address';
      case 'VALIDATION_ERROR':
        return backendError.message || 'Please check your input and try again';
      default:
        if (backendError.message) return backendError.message;
    }
  }

  // Check for specific error codes/types
  const errorCode = error.code || error.response?.status;
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // Check for specific error messages
  const errorMessage = error.message || error.response?.data?.detail || error.response?.data?.message;
  if (errorMessage && ERROR_MESSAGES[errorMessage]) {
    return ERROR_MESSAGES[errorMessage];
  }

  // Use context-specific templates
  const contextTemplate = CONTEXT_TEMPLATES[context];
  if (contextTemplate && contextTemplate[category]) {
    return contextTemplate[category];
  }

  // Fallback to context default
  return contextTemplate?.default || CONTEXT_TEMPLATES.general.default;
};

/**
 * Main function to process any error into a user-friendly format
 */
export const processError = (
  error: any, 
  context: ErrorContext = 'general',
  errorId?: string
): ProcessedError => {
  const category = categorizeError(error);
  const retryable = isRetryableError(error, category);
  const severity = getErrorSeverity(error, category);
  const userMessage = getUserFriendlyMessage(error, category, context);
  
  // Get original error message for debugging
  const originalMessage = error?.message || 
                         error?.response?.data?.detail || 
                         error?.response?.data?.message || 
                         'Unknown error';

  // Get error details for debugging
  const details = error?.stack || 
                 JSON.stringify(error?.response?.data, null, 2) ||
                 undefined;

  return {
    message: originalMessage,
    userMessage,
    category,
    context,
    code: error?.code || error?.response?.status,
    retryable,
    severity,
    details,
    errorId
  };
};

/**
 * Enhanced version of the existing getErrorMessage function
 */
export const getEnhancedErrorMessage = (error: any, context: ErrorContext = 'general'): string => {
  const processed = processError(error, context);
  return processed.userMessage;
};

/**
 * Simple error message extractor for backward compatibility
 */
export const getErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  // Check for user-friendly message first
  if (error.userMessage) return error.userMessage;
  
  // Check for API error message
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  
  // Check for standard error message
  if (error.message) return error.message;
  
  // Fallback
  return 'An unexpected error occurred';
};

/**
 * Checks if the current error is a network connectivity issue
 */
export const isNetworkError = (error: any): boolean => {
  return categorizeError(error) === 'network';
};

/**
 * Checks if the error requires user authentication
 */
export const isAuthenticationError = (error: any): boolean => {
  return categorizeError(error) === 'authentication';
};

/**
 * Gets retry delay in milliseconds with exponential backoff
 */
export const getRetryDelay = (attemptNumber: number, maxDelay: number = 30000): number => {
  const baseDelay = 1000; // 1 second
  const delay = Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay);
  // Add some jitter
  return delay + Math.random() * 1000;
};

/**
 * Default error handler that can be used across components
 */
export const handleError = (
  error: any,
  context: ErrorContext = 'general',
  onRetry?: () => void
): ProcessedError => {
  const processed = processError(error, context, Date.now().toString(36));
  
  // Log error for debugging
  console.group(`🚨 Error (${context})`);
  console.error('Processed Error:', processed);
  console.error('Original Error:', error);
  console.groupEnd();

  return processed;
};